import { Link, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  LayoutDashboard,
  Box,
  Server,
  HardDrive,
  Network,
  Users,
  Wifi,
  WifiOff,
  LogOut,
} from 'lucide-react'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuth } from '../hooks/useAuth'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  // comingSoon marks a nav entry whose backend is not yet implemented. It
  // still routes (to a placeholder) but is labelled so it does not overstate
  // what the dashboard can actually do.
  comingSoon?: boolean
}

const navigation: NavItem[] = [
  { name: 'VMs', href: '/vms', icon: Server },
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Containers', href: '/containers', icon: Box },
  { name: 'Storage', href: '/storage', icon: HardDrive },
  { name: 'Network', href: '/network', icon: Network, comingSoon: true },
  { name: 'Users', href: '/users', icon: Users, comingSoon: true },
]

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { connected } = useWebSocket()
  const { user, me, logout } = useAuth()

  const planName = me?.plan?.name || me?.org?.plan
  const orgLabel = me?.org?.name || user?.email || ''

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-gray-900 flex flex-col">
        <div className="flex h-16 items-center px-6">
          <Link to="/vms" className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-voltainer-500 to-armored-500" />
            <span className="text-xl font-bold text-white">VoltVisor</span>
          </Link>
        </div>

        <nav className="mt-6 px-3 flex-1">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/' && location.pathname.startsWith(item.href))
              const Icon = item.icon

              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={clsx(
                      'flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.name}</span>
                    {item.comingSoon && (
                      <span className="ml-auto rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                        Soon
                      </span>
                    )}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Account + connection status */}
        <div className="p-4 space-y-3 border-t border-gray-800">
          {orgLabel && (
            <div className="px-1">
              <p className="truncate text-sm font-medium text-white">{orgLabel}</p>
              <p className="truncate text-xs text-gray-400">
                {user?.email}
                {planName && <span className="ml-1 text-voltainer-400">· {planName}</span>}
              </p>
            </div>
          )}
          <button
            onClick={logout}
            className="flex w-full items-center space-x-2 rounded-lg px-3 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
          <div className={clsx(
            'flex items-center space-x-2 rounded-lg px-3 py-2 text-sm',
            connected ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
          )}>
            {connected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            <span>{connected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="min-h-screen p-8">
          {children}
        </div>
      </main>
    </div>
  )
}
